'use client';

import { useState } from 'react';
import { Dialog, Button as AsioButton } from '@rich-products-corp/primitives-richproducts';
import {
  getAddressValidationItems,
  getAddressValidationStatus,
  isInvalidAddressValidationStatus,
} from '@/helpers/addressValidationStatus';

export const useAddressValidation = () => {
  const isGoogleEnabled = process.env.NEXT_PUBLIC_ENABLE_GOOGLE_ADDRESS_VALIDATION === 'true';

  const [open, setOpen] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [resolvePromise, setResolvePromise] = useState<any>(null);
  const [isMultiAddress, setIsMultiAddress] = useState(false);

  const getFinalStatus = (data: any) => {
    const statuses = getAddressValidationItems(data).map(getAddressValidationStatus);

    if (statuses.some((status) => status === 'NOTDELIVERABLE')) {
      return 'NOTDELIVERABLE';
    }
    if (statuses.some((status) => status === 'MISMATCH')) {
      return 'MISMATCH';
    }
    if (statuses.some((status) => status === 'DELIVERABILISH')) {
      return 'DELIVERABILISH';
    }
    return 'DELIVERABLE';
  };

  const validateAddress = async (addresses: any) => {
    if (!isGoogleEnabled) return true;

    try {
      const payload = Array.isArray(addresses) ? addresses : [addresses];
      const isMulti = Array.isArray(addresses) && addresses.length > 1;

      const res = await fetch('/api/address/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // API safety
      if (!res.ok) {
        console.error('GAV API failed');
        return true;
      }

      const data = await res.json();
      const finalStatus = getFinalStatus(data);

      if (!isInvalidAddressValidationStatus(finalStatus)) return true;

      return new Promise<boolean>((resolve) => {
        setResponse({
          status: finalStatus,
          raw: data,
        });
        setIsMultiAddress(isMulti);
        setResolvePromise(() => resolve);
        setOpen(true);
      });
    } catch (e) {
      console.error('Validation error', e);
      return true;
    }
  };

  const handleYes = () => {
    resolvePromise?.(true);
    setOpen(false);
    setResponse(null);
  };

  const handleNo = () => {
    resolvePromise?.(false);
    setOpen(false);
    setResponse(null);
  };

  const DialogComponent = () => {
    if (!isGoogleEnabled) return null;

    const renderDialogContent = () => {
      if (!response) return null;

      if (isMultiAddress && response?.raw && Array.isArray(response.raw)) {
        return (
          <div className="py-4">
            <p className="font-semibold text-primary-800">
              One or more addresses are invalid, if you want to edit then click on No else Click on Yes To Proceed.
            </p>
          </div>
        );
      }

      return (
        <div className="py-4">
          <p className="font-semibold text-primary-800">
            The address you entered appears to be invalid. Do you want to continue anyway?
          </p>
        </div>
      );
    };

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <Dialog.Content {...({} as any)} className="max-w-md p-6">
          <Dialog.Header>
            <Dialog.Title>Address Validation</Dialog.Title>
          </Dialog.Header>

          {renderDialogContent()}

          <div className="flex justify-end gap-2">
            <AsioButton variant="secondary" onClick={handleNo}>
              No
            </AsioButton>
            <AsioButton className="text-white" onClick={handleYes}>
              Yes
            </AsioButton>
          </div>
        </Dialog.Content>
      </Dialog>
    );
  };

  return { validateAddress, DialogComponent };
};

